import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileText, X, Loader2, CheckCircle2 } from "lucide-react";
import ConsentCheckboxes from "@/components/ConsentCheckboxes";
import { authHeaders } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCS_CONTRATO = [
  "Título de Residência (quando aplicável)", "Cartão de Cidadão", "Documento CPLP válido (quando aplicável)",
  "Contrato de trabalho", "Últimos 3 recibos de vencimento", "IRS do ano vigente",
  "Comprovativo de morada (água, luz, internet ou telefone)", "Comprovativo de NIB / IBAN",
];
const DOCS_RECIBOS = [
  "Documento de identificação", "Título de residência (quando aplicável)", "Declaração de início de atividade",
  "Recibos verdes", "Comprovativos de rendimentos", "IRS", "Comprovativo de morada", "Comprovativo de NIB / IBAN",
];

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.mp4,.mov";

const emptyPerson = { name: "", email: "", phone: "", address: "", postal: "", locality: "", birth: "", nif: "", situation: "contrato" };

function Section({ title, children, testid }) {
  return (
    <div className="border border-slate-200 bg-white p-5 sm:p-6" data-testid={testid}>
      <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0072C6] mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, required, children, htmlFor }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
        {label}{required && " *"}
      </label>
      {children}
    </div>
  );
}

const inputCls = "w-full border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:border-[#0072C6] bg-white";

function FileGroup({ label, files, onAdd, onRemove, testid, compact }) {
  return (
    <div className="border border-dashed border-slate-300 p-4 bg-[#F8FAFC]" data-testid={testid}>
      <p className="text-xs font-semibold text-slate-700 mb-2">{label}</p>
      <label className="inline-flex items-center gap-2 text-xs font-semibold text-[#0072C6] border border-[#0072C6] px-3 py-2 cursor-pointer hover:bg-[#0072C6] hover:text-white transition-colors">
        <Upload className="w-3.5 h-3.5" /> Adicionar ficheiros
        <input type="file" multiple accept={ACCEPT} className="hidden" onChange={onAdd} data-testid={`${testid}-input`} />
      </label>
      {files.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {files.map((f, i) => (
            <li key={i} className="flex items-center gap-2 bg-white border border-slate-200 px-3 py-1.5 text-xs">
              <FileText className="w-3.5 h-3.5 text-[#0072C6] shrink-0" />
              <span className="flex-1 truncate text-slate-700">{f.name}</span>
              <span className="text-slate-400">{(f.size / 1024).toFixed(0)} KB</span>
              <button type="button" onClick={() => onRemove(i)} aria-label="Remover" className="text-slate-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function FinancingForm({ vehicle, onClose }) {
  const [t1, setT1] = useState({ ...emptyPerson });
  const [modality, setModality] = useState("total");
  const [downPayment, setDownPayment] = useState("");
  const [hasTrade, setHasTrade] = useState(false);
  const [trade, setTrade] = useState({ brand: "", model: "", year: "", km: "", value: "", plate: "", paidoff: "sim", remaining: "" });
  const [hasT2, setHasT2] = useState(false);
  const [t2, setT2] = useState({ ...emptyPerson });
  const [consent, setConsent] = useState({ accept_policy: false, accept_marketing: false });
  const [sending, setSending] = useState(false);
  const [doneId, setDoneId] = useState(null);
  const [showSummary, setShowSummary] = useState(false);

  const [t1Docs, setT1Docs] = useState([]);
  const [t2Docs, setT2Docs] = useState([]);
  const [tradePhotos, setTradePhotos] = useState([]);
  const [tradeVideos, setTradeVideos] = useState([]);
  const [tradeInterior, setTradeInterior] = useState([]);
  const [tradeExterior, setTradeExterior] = useState([]);
  const [tradeOther, setTradeOther] = useState([]);

  const addTo = (setter) => (e) => { setter((p) => [...p, ...Array.from(e.target.files || [])]); e.target.value = ""; };
  const removeFrom = (setter) => (i) => setter((p) => p.filter((_, idx) => idx !== i));

  const setP = (setter) => (k) => (e) => setter((p) => ({ ...p, [k]: e.target.value }));
  const setT = (k) => (e) => setTrade((p) => ({ ...p, [k]: e.target.value }));

  const validate = () => {
    if (!t1.name || !t1.email || !t1.phone || !t1.nif) return "Preencha os dados obrigatórios do primeiro titular (nome, email, telefone, NIF).";
    if (modality === "entrada" && (!downPayment || Number(downPayment) <= 0)) return "Indique o valor da entrada inicial.";
    if (hasTrade && (!trade.brand || !trade.model)) return "Preencha a marca e o modelo da viatura para retoma.";
    if (hasTrade && trade.paidoff === "nao" && !trade.remaining) return "Indique quanto falta para liquidar o financiamento da retoma.";
    if (hasT2 && (!t2.name || !t2.email || !t2.phone || !t2.nif)) return "Preencha os dados obrigatórios do segundo titular.";
    if (!consent.accept_policy) return "Tem de aceitar a Política de Privacidade para enviar o pedido.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    if (!showSummary) { setShowSummary(true); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    setSending(true);
    try {
      const fd = new FormData();
      // vehicle
      fd.append("vehicle_id", vehicle.id || "");
      fd.append("vehicle_brand", vehicle.brand || "");
      fd.append("vehicle_model", vehicle.model || "");
      fd.append("vehicle_title", vehicle.title || "");
      fd.append("vehicle_price", vehicle.price ?? "");
      fd.append("vehicle_year", vehicle.year ?? "");
      fd.append("vehicle_plate", vehicle.plate || "");
      fd.append("vehicle_image", vehicle.image_url || (vehicle.images && vehicle.images[0]) || "");
      // first holder
      Object.entries(t1).forEach(([k, v]) => fd.append(`t1_${k}`, v));
      // financing
      fd.append("modality", modality);
      if (modality === "entrada") fd.append("down_payment", downPayment);
      // trade-in
      fd.append("tradein", hasTrade ? "sim" : "nao");
      if (hasTrade) Object.entries(trade).forEach(([k, v]) => fd.append(`ti_${k}`, v));
      // second holder
      fd.append("t2_has", hasT2 ? "sim" : "nao");
      if (hasT2) Object.entries(t2).forEach(([k, v]) => fd.append(`t2_${k}`, v));
      // consent
      fd.append("consent_policy", String(consent.accept_policy));
      fd.append("consent_marketing", String(consent.accept_marketing));
      // files
      t1Docs.forEach((f) => fd.append("t1_docs", f));
      if (hasT2) t2Docs.forEach((f) => fd.append("t2_docs", f));
      if (hasTrade) {
        tradePhotos.forEach((f) => fd.append("tradein_photos", f));
        tradeVideos.forEach((f) => fd.append("tradein_videos", f));
        tradeInterior.forEach((f) => fd.append("tradein_interior", f));
        tradeExterior.forEach((f) => fd.append("tradein_exterior", f));
        tradeOther.forEach((f) => fd.append("tradein_other", f));
      }
      const res = await axios.post(`${API}/financing/request`, fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      setDoneId(res.data.id);
      toast.success("Pedido de financiamento enviado com sucesso!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar o pedido. Tente novamente.");
      setShowSummary(false);
    } finally {
      setSending(false);
    }
  };

  const fmtPrice = (n) => (n == null ? "—" : Number(n).toLocaleString("pt-PT") + " €");

  if (doneId) {
    return (
      <div className="border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="financing-success">
        <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
        <h3 className="font-display text-2xl text-[#0072C6] mb-2">Pedido Enviado</h3>
        <p className="text-sm text-slate-600">O seu pedido de financiamento foi registado com sucesso.</p>
        <p className="mt-3 text-sm font-semibold text-slate-700">Número do pedido: <span className="font-mono">{doneId.slice(0, 8).toUpperCase()}</span></p>
        <p className="mt-2 text-xs text-slate-500">A nossa equipa irá analisar e entrará em contacto consigo.</p>
        <button onClick={onClose} data-testid="financing-close" className="mt-6 inline-block bg-[#0072C6] text-white text-sm font-semibold px-7 py-3 hover:bg-[#005FA3] transition-colors">Fechar</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="financing-form">
      {/* Vehicle summary */}
      <Section title="Viatura Pretendida" testid="fin-vehicle">
        <div className="flex items-center gap-4">
          {(vehicle.image_url || (vehicle.images && vehicle.images[0])) && (
            <img src={vehicle.image_url || vehicle.images[0]} alt={vehicle.title} className="w-20 h-14 object-cover border border-slate-200" />
          )}
          <div>
            <p className="font-semibold text-[#0072C6]">{vehicle.brand} {vehicle.model}</p>
            <p className="text-sm text-slate-600">{vehicle.title}{vehicle.year ? ` · ${vehicle.year}` : ""}</p>
            <p className="font-display text-lg text-slate-900 mt-1">{fmtPrice(vehicle.price)}</p>
          </div>
        </div>
      </Section>

      {/* Dados do primeiro titular */}
      <Section title="Dados do Primeiro Titular" testid="fin-t1">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nome completo" required htmlFor="t1-name"><input id="t1-name" data-testid="t1-name" required className={inputCls} value={t1.name} onChange={setP(setT1)("name")} /></Field>
          <Field label="Email" required htmlFor="t1-email"><input id="t1-email" type="email" data-testid="t1-email" required className={inputCls} value={t1.email} onChange={setP(setT1)("email")} /></Field>
          <Field label="Telefone" required htmlFor="t1-phone"><input id="t1-phone" data-testid="t1-phone" required className={inputCls} value={t1.phone} onChange={setP(setT1)("phone")} /></Field>
          <Field label="NIF" required htmlFor="t1-nif"><input id="t1-nif" data-testid="t1-nif" required className={inputCls} value={t1.nif} onChange={setP(setT1)("nif")} /></Field>
          <div className="sm:col-span-2"><Field label="Morada completa" htmlFor="t1-address"><input id="t1-address" data-testid="t1-address" className={inputCls} value={t1.address} onChange={setP(setT1)("address")} /></Field></div>
          <Field label="Código postal" htmlFor="t1-postal"><input id="t1-postal" data-testid="t1-postal" className={inputCls} value={t1.postal} onChange={setP(setT1)("postal")} /></Field>
          <Field label="Localidade" htmlFor="t1-locality"><input id="t1-locality" data-testid="t1-locality" className={inputCls} value={t1.locality} onChange={setP(setT1)("locality")} /></Field>
          <Field label="Data de nascimento" htmlFor="t1-birth"><input id="t1-birth" type="date" data-testid="t1-birth" className={inputCls} value={t1.birth} onChange={setP(setT1)("birth")} /></Field>
        </div>
      </Section>

      {/* Condições do financiamento */}
      <Section title="Condições do Financiamento" testid="fin-conditions">
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <button type="button" onClick={() => setModality("entrada")} data-testid="modality-entrada"
            className={`py-3 text-sm font-semibold border transition-colors ${modality === "entrada" ? "bg-[#0072C6] text-white border-[#0072C6]" : "border-slate-300 text-slate-600 hover:border-[#0072C6]"}`}>
            Com Entrada Inicial
          </button>
          <button type="button" onClick={() => { setModality("total"); setDownPayment(""); }} data-testid="modality-total"
            className={`py-3 text-sm font-semibold border transition-colors ${modality === "total" ? "bg-[#0072C6] text-white border-[#0072C6]" : "border-slate-300 text-slate-600 hover:border-[#0072C6]"}`}>
            Financiamento a 100% (sem entrada)
          </button>
        </div>
        {modality === "entrada" && (
          <Field label="Valor da entrada inicial (€)" required htmlFor="down-payment">
            <input id="down-payment" type="number" min="1" data-testid="down-payment" className={inputCls} value={downPayment} onChange={(e) => setDownPayment(e.target.value)} placeholder="Ex.: 5000" />
          </Field>
        )}
        {modality === "total" && <p className="text-xs text-slate-500">Entrada registada automaticamente como 0 € (financiamento a 100%).</p>}
      </Section>

      {/* Retoma */}
      <Section title="Tem uma Viatura para Retoma?" testid="fin-tradein">
        <div className="grid grid-cols-2 gap-3 mb-4">
          <button type="button" onClick={() => setHasTrade(true)} data-testid="tradein-yes"
            className={`py-3 text-sm font-semibold border transition-colors ${hasTrade ? "bg-[#0072C6] text-white border-[#0072C6]" : "border-slate-300 text-slate-600"}`}>Sim</button>
          <button type="button" onClick={() => setHasTrade(false)} data-testid="tradein-no"
            className={`py-3 text-sm font-semibold border transition-colors ${!hasTrade ? "bg-[#0072C6] text-white border-[#0072C6]" : "border-slate-300 text-slate-600"}`}>Não</button>
        </div>
        {hasTrade && (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Marca" required htmlFor="ti-brand"><input id="ti-brand" data-testid="ti-brand" className={inputCls} value={trade.brand} onChange={setT("brand")} /></Field>
              <Field label="Modelo" required htmlFor="ti-model"><input id="ti-model" data-testid="ti-model" className={inputCls} value={trade.model} onChange={setT("model")} /></Field>
              <Field label="Ano" htmlFor="ti-year"><input id="ti-year" type="number" data-testid="ti-year" className={inputCls} value={trade.year} onChange={setT("year")} /></Field>
              <Field label="Quilometragem" htmlFor="ti-km"><input id="ti-km" type="number" data-testid="ti-km" className={inputCls} value={trade.km} onChange={setT("km")} /></Field>
              <Field label="Valor pretendido (€)" htmlFor="ti-value"><input id="ti-value" type="number" data-testid="ti-value" className={inputCls} value={trade.value} onChange={setT("value")} /></Field>
              <Field label="Matrícula" htmlFor="ti-plate"><input id="ti-plate" data-testid="ti-plate" className={inputCls} value={trade.plate} onChange={setT("plate")} /></Field>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Viatura quitada?</p>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setT("paidoff")({ target: { value: "sim" } })} data-testid="ti-paidoff-yes"
                  className={`py-2.5 text-sm font-semibold border transition-colors ${trade.paidoff === "sim" ? "bg-[#0072C6] text-white border-[#0072C6]" : "border-slate-300 text-slate-600"}`}>Sim</button>
                <button type="button" onClick={() => setT("paidoff")({ target: { value: "nao" } })} data-testid="ti-paidoff-no"
                  className={`py-2.5 text-sm font-semibold border transition-colors ${trade.paidoff === "nao" ? "bg-[#0072C6] text-white border-[#0072C6]" : "border-slate-300 text-slate-600"}`}>Não</button>
              </div>
              {trade.paidoff === "nao" && (
                <div className="mt-4">
                  <Field label="Quanto falta para liquidar o financiamento atual? (€)" required htmlFor="ti-remaining">
                    <input id="ti-remaining" type="number" min="0" data-testid="ti-remaining" className={inputCls} value={trade.remaining} onChange={setT("remaining")} placeholder="Ex.: 2000" />
                  </Field>
                </div>
              )}
            </div>
            <div className="space-y-3">
              <FileGroup label="Fotografias da viatura" files={tradePhotos} onAdd={addTo(setTradePhotos)} onRemove={removeFrom(setTradePhotos)} testid="trade-photos" />
              <FileGroup label="Vídeos da viatura" files={tradeVideos} onAdd={addTo(setTradeVideos)} onRemove={removeFrom(setTradeVideos)} testid="trade-videos" />
              <FileGroup label="Fotografias do interior" files={tradeInterior} onAdd={addTo(setTradeInterior)} onRemove={removeFrom(setTradeInterior)} testid="trade-interior" />
              <FileGroup label="Fotografias do exterior" files={tradeExterior} onAdd={addTo(setTradeExterior)} onRemove={removeFrom(setTradeExterior)} testid="trade-exterior" />
              <FileGroup label="Outros documentos da retoma" files={tradeOther} onAdd={addTo(setTradeOther)} onRemove={removeFrom(setTradeOther)} testid="trade-other" />
            </div>
          </div>
        )}
      </Section>

      {/* Documentação do primeiro titular */}
      <Section title="Documentação do Primeiro Titular" testid="fin-t1-docs">
        <div className="grid grid-cols-2 gap-2 mb-4 border border-slate-200 p-1">
          <button type="button" onClick={() => setP(setT1)("situation")({ target: { value: "contrato" } })} data-testid="t1-sit-contrato"
            className={`py-2.5 text-sm font-semibold transition-colors ${t1.situation === "contrato" ? "bg-[#0072C6] text-white" : "text-slate-600"}`}>Contrato de Trabalho</button>
          <button type="button" onClick={() => setP(setT1)("situation")({ target: { value: "recibos" } })} data-testid="t1-sit-recibos"
            className={`py-2.5 text-sm font-semibold transition-colors ${t1.situation === "recibos" ? "bg-[#0072C6] text-white" : "text-slate-600"}`}>Recibos Verdes</button>
        </div>
        <ul className="space-y-2 mb-4" data-testid="t1-doc-list">
          {(t1.situation === "contrato" ? DOCS_CONTRATO : DOCS_RECIBOS).map((d) => (
            <li key={d} className="flex items-start gap-2 text-xs text-slate-600"><FileText className="w-3.5 h-3.5 text-[#0072C6] shrink-0 mt-0.5" /> {d}</li>
          ))}
        </ul>
        <FileGroup label="Enviar os seus documentos (PDF, JPG, PNG, DOC, DOCX, XLS, XLSX, MP4, MOV — até 25 MB cada)" files={t1Docs} onAdd={addTo(setT1Docs)} onRemove={removeFrom(setT1Docs)} testid="t1-docs" />
      </Section>

      {/* Segundo titular */}
      <Section title="Pretende Adicionar um Segundo Titular ao Financiamento?" testid="fin-t2-toggle">
        <div className="grid grid-cols-2 gap-3">
          <button type="button" onClick={() => setHasT2(true)} data-testid="t2-yes"
            className={`py-3 text-sm font-semibold border transition-colors ${hasT2 ? "bg-[#0072C6] text-white border-[#0072C6]" : "border-slate-300 text-slate-600"}`}>Sim</button>
          <button type="button" onClick={() => setHasT2(false)} data-testid="t2-no"
            className={`py-3 text-sm font-semibold border transition-colors ${!hasT2 ? "bg-[#0072C6] text-white border-[#0072C6]" : "border-slate-300 text-slate-600"}`}>Não</button>
        </div>
      </Section>

      {hasT2 && (
        <>
          <Section title="Dados do Segundo Titular" testid="fin-t2">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nome completo" required htmlFor="t2-name"><input id="t2-name" data-testid="t2-name" required className={inputCls} value={t2.name} onChange={setP(setT2)("name")} /></Field>
              <Field label="Email" required htmlFor="t2-email"><input id="t2-email" type="email" data-testid="t2-email" required className={inputCls} value={t2.email} onChange={setP(setT2)("email")} /></Field>
              <Field label="Telefone" required htmlFor="t2-phone"><input id="t2-phone" data-testid="t2-phone" required className={inputCls} value={t2.phone} onChange={setP(setT2)("phone")} /></Field>
              <Field label="NIF" required htmlFor="t2-nif"><input id="t2-nif" data-testid="t2-nif" required className={inputCls} value={t2.nif} onChange={setP(setT2)("nif")} /></Field>
              <div className="sm:col-span-2"><Field label="Morada completa" htmlFor="t2-address"><input id="t2-address" data-testid="t2-address" className={inputCls} value={t2.address} onChange={setP(setT2)("address")} /></Field></div>
              <Field label="Código postal" htmlFor="t2-postal"><input id="t2-postal" data-testid="t2-postal" className={inputCls} value={t2.postal} onChange={setP(setT2)("postal")} /></Field>
              <Field label="Localidade" htmlFor="t2-locality"><input id="t2-locality" data-testid="t2-locality" className={inputCls} value={t2.locality} onChange={setP(setT2)("locality")} /></Field>
              <Field label="Data de nascimento" htmlFor="t2-birth"><input id="t2-birth" type="date" data-testid="t2-birth" className={inputCls} value={t2.birth} onChange={setP(setT2)("birth")} /></Field>
            </div>
          </Section>
          <Section title="Documentação do Segundo Titular" testid="fin-t2-docs">
            <div className="grid grid-cols-2 gap-2 mb-4 border border-slate-200 p-1">
              <button type="button" onClick={() => setP(setT2)("situation")({ target: { value: "contrato" } })} data-testid="t2-sit-contrato"
                className={`py-2.5 text-sm font-semibold transition-colors ${t2.situation === "contrato" ? "bg-[#0072C6] text-white" : "text-slate-600"}`}>Contrato de Trabalho</button>
              <button type="button" onClick={() => setP(setT2)("situation")({ target: { value: "recibos" } })} data-testid="t2-sit-recibos"
                className={`py-2.5 text-sm font-semibold transition-colors ${t2.situation === "recibos" ? "bg-[#0072C6] text-white" : "text-slate-600"}`}>Recibos Verdes</button>
            </div>
            <ul className="space-y-2 mb-4">
              {(t2.situation === "contrato" ? DOCS_CONTRATO : DOCS_RECIBOS).map((d) => (
                <li key={d} className="flex items-start gap-2 text-xs text-slate-600"><FileText className="w-3.5 h-3.5 text-[#0072C6] shrink-0 mt-0.5" /> {d}</li>
              ))}
            </ul>
            <FileGroup label="Enviar os documentos do segundo titular" files={t2Docs} onAdd={addTo(setT2Docs)} onRemove={removeFrom(setT2Docs)} testid="t2-docs" />
          </Section>
        </>
      )}

      {/* Consent + summary */}
      <Section title="Resumo e Consentimento" testid="fin-consent">
        {showSummary && (
          <div className="mb-5 bg-[#F8FAFC] border border-slate-200 p-4 text-sm space-y-1.5" data-testid="fin-summary">
            <p><strong>Viatura:</strong> {vehicle.brand} {vehicle.model} — {fmtPrice(vehicle.price)}</p>
            <p><strong>Titular:</strong> {t1.name} ({t1.email})</p>
            <p><strong>Financiamento:</strong> {modality === "total" ? "100% (sem entrada)" : `Com entrada de ${fmtPrice(Number(downPayment))}`}</p>
            <p><strong>Retoma:</strong> {hasTrade ? `${trade.brand} ${trade.model} (${trade.paidoff === "sim" ? "quitada" : `falta ${fmtPrice(Number(trade.remaining))}`})` : "Não"}</p>
            <p><strong>Segundo titular:</strong> {hasT2 ? t2.name : "Não"}</p>
            <p><strong>Documentos:</strong> {t1Docs.length + (hasT2 ? t2Docs.length : 0) + (hasTrade ? tradePhotos.length + tradeVideos.length + tradeInterior.length + tradeExterior.length + tradeOther.length : 0)} ficheiro(s)</p>
          </div>
        )}
        <p className="text-xs text-slate-500 mb-4">Os seus dados serão tratados para análise do pedido de financiamento e partilhados apenas com as entidades financeiras envolvidas.</p>
        <ConsentCheckboxes consent={consent} onChange={setConsent} idPrefix="financing" />
      </Section>

      <div className="flex flex-wrap gap-3">
        <button type="submit" disabled={sending} data-testid="financing-submit"
          className="inline-flex items-center gap-2 bg-[#FF6A00] hover:bg-[#e65f00] disabled:opacity-60 text-white text-sm font-semibold px-8 py-3.5 transition-colors">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {sending ? "A enviar…" : showSummary ? "Confirmar e Enviar Pedido de Financiamento" : "Rever e Enviar Pedido de Financiamento"}
        </button>
        {showSummary && (
          <button type="button" onClick={() => setShowSummary(false)} data-testid="financing-back"
            className="border border-slate-300 text-slate-600 text-sm font-semibold px-6 py-3.5 hover:border-slate-400 transition-colors">
            Voltar e Editar
          </button>
        )}
        <button type="button" onClick={onClose} data-testid="financing-cancel"
          className="border border-slate-300 text-slate-600 text-sm font-semibold px-6 py-3.5 hover:border-slate-400 transition-colors">
          Cancelar
        </button>
      </div>
    </form>
  );
}
